/**
 * User Preferences Module
 * Manages user preferences in localStorage (redux-state.preferences)
 * These persist across sessions/logouts
 *
 * Layout preference is also synced to a cookie for server-side rendering
 */

const STORAGE_KEY = 'redux-state';
const LAYOUT_COOKIE_NAME = 'layout_pref';
const THEME_COOKIE_NAME = 'theme_pref';

/**
 * Get the full redux-state object from localStorage
 */
function getState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    console.error('[Preferences] Error reading state:', e);
    return {};
  }
}

/**
 * Save state to localStorage
 */
function setState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('[Preferences] Error saving state:', e);
  }
}

/**
 * Set a cookie with the given name and value
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days (default: 365)
 */
function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === 'https:' ? 'Secure;' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; ${secure}SameSite=Lax`;
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
}

/**
 * Get layout preference from localStorage
 * @returns {string|null} 'sidebar' or 'ultra-legacy' or null
 */
export function getLayoutPreference() {
  return getState().preferences?.layout || null;
}

/**
 * Set layout preference in localStorage and sync to cookie
 * @param {string} layout - 'sidebar' or 'ultra-legacy'
 */
export function setLayoutPreference(layout) {
  const state = getState();
  state.preferences = state.preferences || {};
  state.preferences.layout = layout;
  state.preferences.layoutExplicit = true;
  setState(state);

  // Sync to cookie for server-side rendering
  setCookie(LAYOUT_COOKIE_NAME, layout);
}

/**
 * Check if current page layout matches localStorage preference
 * If mismatch, redirect to correct layout
 * Keeps localStorage and cookie in sync bidirectionally
 * @param {string} currentLayout - The layout PHP rendered
 */
export function ensureCorrectLayout(currentLayout) {
  const state = getState();
  const preferredLayout = state.preferences?.layout || null;
  const isExplicit = state.preferences?.layoutExplicit || false;
  const cookieValue = getCookie(LAYOUT_COOKIE_NAME);

  // If no localStorage preference, use cookie or current layout
  if (!preferredLayout) {
    // Cookie exists but localStorage doesn't - sync cookie to localStorage
    // but don't mark as explicit (cookie may have been set by server defaults)
    if (cookieValue && ['sidebar', 'ultra-legacy'].includes(cookieValue)) {
      const s = getState();
      s.preferences = s.preferences || {};
      s.preferences.layout = cookieValue;
      setState(s);
      setCookie(LAYOUT_COOKIE_NAME, cookieValue);
      return;
    }
    // Neither exists - save current layout to both (not explicit)
    const s = getState();
    s.preferences = s.preferences || {};
    s.preferences.layout = currentLayout;
    setState(s);
    setCookie(LAYOUT_COOKIE_NAME, currentLayout);
    return;
  }

  // Only sync cookie and redirect when user has made an explicit choice
  if (isExplicit && cookieValue !== preferredLayout) {
    setCookie(LAYOUT_COOKIE_NAME, preferredLayout);
  }

  if (isExplicit && preferredLayout !== currentLayout) {
    const url = new URL(window.location.href);
    url.searchParams.set('layout', preferredLayout);
    window.location.replace(url.toString());
  }
}

/**
 * Handle ?layout=X query param and save to localStorage
 */
export function handleLayoutQueryParam() {
  const urlParams = new URLSearchParams(window.location.search);
  const layoutParam = urlParams.get('layout');

  if (layoutParam && ['sidebar', 'ultra-legacy'].includes(layoutParam)) {
    setLayoutPreference(layoutParam);

    // Remove the query param from URL
    urlParams.delete('layout');
    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
    window.history.replaceState({}, '', newUrl);
  }
}

/**
 * Persist layout preference to server (DB + cookie set in response)
 * @param {string} layout - 'sidebar' or 'ultra-legacy'
 */
export function saveLayoutPreferenceToServer(layout) {
  const formData = new FormData();
  formData.append('layout', layout);
  const token = document.querySelector('meta[name="csrf_token"]');
  if (token) formData.append('csrf_token', token.getAttribute('content'));
  fetch('/app/save-layout-preference', {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  }).catch(() => {
    // Server save is best-effort; localStorage/cookie already set
  });
}

// ─── Theme preference ───

/**
 * Get theme preference from localStorage
 * @returns {string|null} 'light' or 'dark' or null
 */
export function getThemePreference() {
  return getState().preferences?.theme || null;
}

/**
 * Set theme preference in localStorage and sync to cookie
 * @param {string} theme - 'light' or 'dark'
 */
export function setThemePreference(theme) {
  const state = getState();
  state.preferences = state.preferences || {};
  state.preferences.theme = theme;
  setState(state);
  setCookie(THEME_COOKIE_NAME, theme);
}

/**
 * Apply theme by toggling dark class on <html>
 * @param {string} theme - 'light' or 'dark'
 */
export function applyTheme(theme) {
  var layoutCookie = document.cookie.match('(?:^|; )layout_pref=([^;]*)');
  var layout = layoutCookie ? decodeURIComponent(layoutCookie[1]) : null;
  if (theme === 'dark' && layout !== 'ultra-legacy') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Toggle between light and dark theme
 * Persists to localStorage, cookie, and server
 * @returns {string} The new theme ('light' or 'dark')
 */
export function toggleTheme() {
  const current = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const next = current === 'dark' ? 'light' : 'dark';
  setThemePreference(next);
  applyTheme(next);
  saveThemePreferenceToServer(next);
  return next;
}

/**
 * Persist theme preference to server (DB + cookie set in response)
 * @param {string} theme - 'light' or 'dark'
 */
export function saveThemePreferenceToServer(theme) {
  const formData = new FormData();
  formData.append('theme', theme);
  const token = document.querySelector('meta[name="csrf_token"]');
  if (token) formData.append('csrf_token', token.getAttribute('content'));
  fetch('/app/save-theme-preference', {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  }).catch(() => {
    // Best-effort; localStorage/cookie already set
  });
}

/**
 * Clear auth data on logout but preserve preferences
 */
export function clearAuthData() {
  const state = getState();
  // Keep only layout preference, clear auth, account, and theme
  const prefs = state.preferences || {};
  delete prefs.theme;
  setState({
    preferences: prefs
  });
  // Clear theme cookie so it doesn't bleed into next login
  document.cookie = 'theme_pref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  // Remove dark class immediately
  document.documentElement.classList.remove('dark');
}

// Expose functions globally
if (typeof window !== 'undefined') {
  window.getLayoutPreference = getLayoutPreference;
  window.setLayoutPreference = setLayoutPreference;
  window.saveLayoutPreferenceToServer = saveLayoutPreferenceToServer;
  window.ensureCorrectLayout = ensureCorrectLayout;
  window.handleLayoutQueryParam = handleLayoutQueryParam;
  window.clearAuthData = clearAuthData;
  window.getThemePreference = getThemePreference;
  window.setThemePreference = setThemePreference;
  window.applyTheme = applyTheme;
  window.toggleTheme = toggleTheme;
  window.saveThemePreferenceToServer = saveThemePreferenceToServer;
}
